import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useTransactionsList } from '@/api/rpc/hooks/finance/useTransactions';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { formatCurrency } from '@erp/shared-utils';
import { T } from 'gt-react';

const DATE_DISPLAY_FORMAT = 'MMM d, yyyy';

interface FinancialTransactionPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

// NOTE: param _entityType currently unused (kept via '_' prefix) — revisit and delete if not needed
export function FinancialTransactionPreview({
  entityType: _entityType,
  entityId,
  className,
}: FinancialTransactionPreviewProps) {
  // There is no single-entity hook; filter from list for preview purposes
  const { transactions } = useTransactionsList({});
  const transaction = transactions.find((t) => t.id === entityId);

  if (!transaction) {
    return (
      <div className={cn('min-w-56 max-w-sm p-2', className)}>
        <p className="text-xs text-muted-foreground">
          <T context="loading state text for financial transaction preview">
            Loading…
          </T>
        </p>
      </div>
    );
  }

  const amountNumber = Number(transaction.amount || 0);
  const amountAbsFormatted = formatCurrency(
    Math.abs(amountNumber),
    transaction.currency || 'USD',
  );
  const formattedAmount =
    amountNumber < 0 ? `(${amountAbsFormatted})` : amountAbsFormatted;
  const accountLabel = transaction.accountName || 'Operating Account (mock)';

  const directionVariant: 'secondary' | 'outline' =
    (transaction.direction || '').toLowerCase() === 'credit'
      ? 'secondary'
      : 'outline';

  return (
    <div className={cn('min-w-56 max-w-sm p-2', className)}>
      <div className="flex items-center justify-between">
        <div className="font-mono font-semibold">{formattedAmount}</div>
        <Badge variant={directionVariant} className="text-[10px] py-0.5 px-1.5">
          {(transaction.direction || '').toUpperCase() || '—'}
        </Badge>
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        <span>
          {transaction.transactionDate
            ? (() => {
                try {
                  return format(
                    new Date(transaction.transactionDate),
                    DATE_DISPLAY_FORMAT,
                  );
                } catch {
                  return transaction.transactionDate;
                }
              })()
            : '—'}
        </span>
        {transaction.merchantName && (
          <>
            <span className="mx-1.5">•</span>
            <span>{transaction.merchantName}</span>
          </>
        )}
      </div>

      {transaction.description && (
        <div className="mt-1 text-xs line-clamp-1">
          {transaction.description}
        </div>
      )}

      <div className="mt-1 text-[11px] text-muted-foreground">
        <span>
          <T context="label abbreviation for bank account in transaction preview">
            Acct:
          </T>
        </span>{' '}
        <span>{accountLabel}</span>
      </div>
    </div>
  );
}
